import { constant } from '@config/BaseSetting';
import ClientRoutes from '@config/Routes/WebClientRoutes';
import { showFixedAlert } from '@features/application/applicationSlice';
import useForm from '@hooks/useForm';
import useLocalStorage from '@hooks/useLocalStorage';
import { useEditSmtpSettingsMutation } from '@services/WebApiService/ResellerSettingsApi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

const formSchema = yup.object().shape({
  supportTicket: yup.string().required('Support Ticket is required').default(''),
  escalationTicket: yup.string().default(''),
  description: yup.string().default(''),
  mailerMethod: yup.string().required('Mailer Method is required'),
  fromName: yup.string().when('mailerMethod', {
    is: 'custom',
    then: (schema) => schema.required('From name is required'),
  }),
  username: yup.string().when('mailerMethod', {
    is: 'custom',
    then: (schema) => schema.required('Username is required'),
  }),
  changeEmail: yup.bool().default(false),
  email: yup.string().email('Invalid email format').default(''),
  changePassword: yup.bool().default(false),
  password: yup.string().default(''),
  host: yup.string().when('mailerMethod', {
    is: 'custom',
    then: (schema) => schema.required('Host is required'),
  }),
  port: yup.string().when('mailerMethod', {
    is: 'custom',
    then: (schema) => schema.required('Port is required').matches(/^\d+$/, 'Port must be a number'),
  }),
  authentication: yup.string().when('mailerMethod', {
    is: 'custom',
    then: (schema) => schema.required('Authentication is required'),
  }),
  security: yup.string().when('mailerMethod', {
    is: 'custom',
    then: (schema) => schema.required('Security is required'),
  }),
  smtpAutoTestEnabled: yup.bool(),
  testEmailDestination: yup.string().when('smtpAutoTestEnabled', {
    is: true,
    then: (schema) => schema.required('Test Email Destination is required'),
  }),
  testPeriodicity: yup.string().when('smtpAutoTestEnabled', {
    is: true,
    then: (schema) => schema.required('Test Periodicity is required'),
  }),
});

export default function useEditSmtpSettings({
  resellerId,
  original,
  showModal,
  onSuccessRequest,
  onFailedRequest,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [editSmtpSettings, editSmtpSettingsState] = useEditSmtpSettingsMutation();
  const form = useForm(formSchema);

  const resetAll = () => {
    editSmtpSettingsState.reset();
    form.reset();

    const formValues = {
      supportTicket: '',
      description: '',
      escalationTicket: '',
      mailerMethod: original.useCustomSmtp ? 'custom' : 'default',
      fromName: original.fromName || '',
      username: original.username || '',
      changeEmail: false,
      email: '',
      changePassword: false,
      password: '',
      host: original.host || '',
      port: `${original.port}`,
      authentication: original.authentication || '',
      smtpAutoTestEnabled: original.smtpAutoTestEnabled,
      testEmailDestination: original.testEmailDestination || '',
      testPeriodicity: original.testPeriodicity || '',
    };

    if (original.ssl) {
      formValues.security = 'ssl';
    } else if (original.startTls) {
      formValues.security = 'start_tls';
    } else {
      formValues.security = 'none';
    }

    form.set(formValues);
  };

  useEffect(() => {
    resetAll();
  }, [original, showModal]);

  useEffect(() => {
    if (!form.isValid) return;

    const params = {
      'support-ticket': form.values.supportTicket,
      description: form.values.description,
      'escalation-ticket': form.values.escalationTicket,
      reseller_id: resellerId,
      mailer_method: form.values.mailerMethod,
      smtp_auto_test_enabled: form.values.smtpAutoTestEnabled,
    };

    if (form.values.mailerMethod === 'custom') {
      params.from_name = form.values.fromName;
      params.username = form.values.username;
      params.host = form.values.host;
      params.port = Number.parseInt(form.values.port, 10);
      params.authentication = form.values.authentication;
      params.security = form.values.security;

      if (form.values.changeEmail) params.email = form.values.email;
      if (form.values.changePassword) params.password = form.values.password;
    }

    if (form.values.smtpAutoTestEnabled) {
      params.test_email_destination = form.values.testEmailDestination;
      params.test_periodicity = form.values.testPeriodicity;
    }

    editSmtpSettings({
      accessToken: accessTokenSet,
      params,
    });
  }, [form.isValid]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    form.validate();
  };

  useEffect(() => {
    if (editSmtpSettingsState.data?.['status-code'] === 200) {
      if (editSmtpSettingsState.data?.data?.serviceTypeCode) {
        const link = ClientRoutes.toolsRequestStatusPath({
          'service-type': editSmtpSettingsState.data?.data?.serviceTypeCode,
        });
        dispatch(
          showFixedAlert({
            descriptionHtml: t('partner_list_page.edit_smtp_settings.success_submitted_with_link', {
              link,
            }),
          })
        );
      } else {
        dispatch(
          showFixedAlert({
            descriptionHtml: t('partner_list_page.edit_smtp_settings.success_submitted'),
          })
        );
      }
      if (onSuccessRequest) onSuccessRequest();
      resetAll();
    } else if (editSmtpSettingsState.error && onFailedRequest) {
      form.reset(true);
      onFailedRequest(
        editSmtpSettingsState.error?.data?.message ||
          editSmtpSettingsState.error?.data?.['status-code'] ||
          editSmtpSettingsState.error?.status ||
          (editSmtpSettingsState.error.name === 'FetchError'
            ? 'Network error occurred'
            : 'Unknown error occurred')
      );
    }
  }, [editSmtpSettingsState.data, editSmtpSettingsState.error]);

  return {
    fetching: editSmtpSettingsState.isLoading,
    form,
    handleSubmit,
    resetAll,
  };
}
